import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { CdkDragDrop, CdkDrag, CdkDragPlaceholder, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { ModalTitle } from '@components/modal-title/modal-title';
import { SectionHeader } from '@components/section-header/section-header';
import { ProspectService } from '@services/prospect-service';
import { CatalogService } from '@services/catalog-service';
import { CatalogService as CatalogCoreService } from '@intelishs-group/catalog-core';
import { FileManagerService } from '@services/filemanager-service';
import { CifService } from '@integrations/cif';
import type { GenericCatalog } from '@schemas/billing';
import type { ProcesarCifResponse } from '@integrations/cif';
import { GoogleCalendarService } from '@services/google-calendar.service';
import { NotifyService } from '@intelishs-group/notify-service';
import { environment } from 'src/environments/environment';
import { toLocalISOString } from '@shared/utils/date.utils';
import { MEETING_LINK_REGEX } from '@shared/utils/meeting-link.validator';
import type { VEtapaProspectoDTO, Prospect, FileDTO } from '@schemas/prospect';
interface DynamicFormFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select';
  group: string;
  visible: boolean;
  order: number;
  required?: boolean;
  placeholder?: string;
  pattern?: RegExp;
  errorTip?: string;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  selectMode?: 'default' | 'multiple';
}

const RFC_REGEX = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s()-]{7,15}$/;
const CP_REGEX = /^\d{5}$/;

const CONSTANCIA_FISCAL_TYPE_ID = 2;

const EXTRA_FIELDS: DynamicFormFieldConfig[] = [
  // Datos del Prospecto
  { key: 'snombrecomercial', label: 'Nombre Comercial', type: 'text', group: 'Prospecto', visible: false, order: 0, placeholder: 'Nombre comercial', minLength: 2, maxLength: 150, errorTip: 'Mínimo 2 caracteres, máximo 150' },
  { key: 'sactividad', label: 'Actividad', type: 'text', group: 'Prospecto', visible: false, order: 1, placeholder: 'Actividad o giro', minLength: 2, maxLength: 100, errorTip: 'Mínimo 2 caracteres, máximo 100' },
  { key: 'iempleados', label: 'Empleados', type: 'number', group: 'Prospecto', visible: false, order: 2, placeholder: 'Número de empleados', min: 1, max: 999999, errorTip: 'Debe ser un número entre 1 y 999,999' },
  // Dirección del Prospecto
  { key: 'scodigopostal', label: 'Código Postal', type: 'text', group: 'Prospecto', visible: false, order: 3, placeholder: 'Ej. 06600', pattern: CP_REGEX, maxLength: 5, errorTip: 'Código postal de 5 dígitos' },
  { key: 'sdireccion', label: 'Dirección', type: 'text', group: 'Prospecto', visible: false, order: 4, placeholder: 'Calle o avenida', maxLength: 200, errorTip: 'Máximo 200 caracteres' },
  { key: 'snumeroexterior', label: 'Número Exterior', type: 'text', group: 'Prospecto', visible: false, order: 5, placeholder: 'Núm. exterior', maxLength: 20, errorTip: 'Máximo 20 caracteres' },
  { key: 'snumerointerior', label: 'Número Interior', type: 'text', group: 'Prospecto', visible: false, order: 6, placeholder: 'Núm. interior', maxLength: 20, errorTip: 'Máximo 20 caracteres' },
  { key: 'scolonia', label: 'Colonia', type: 'text', group: 'Prospecto', visible: false, order: 7, placeholder: 'Colonia', maxLength: 100, errorTip: 'Máximo 100 caracteres' },
  { key: 'slocalidad', label: 'Estado', type: 'text', group: 'Prospecto', visible: false, order: 8, placeholder: 'Estado', maxLength: 100, errorTip: 'Máximo 100 caracteres' },
  { key: 'smunicipio', label: 'Municipio', type: 'text', group: 'Prospecto', visible: false, order: 9, placeholder: 'Municipio o alcaldía', maxLength: 100, errorTip: 'Máximo 100 caracteres' },
  { key: 'regimenesfiscales', label: 'Regímenes Fiscales', type: 'select', group: 'Prospecto', visible: false, order: 10, placeholder: 'Selecciona régimen fiscal', selectMode: 'multiple', errorTip: 'Selecciona al menos un régimen' },
  // Contacto
  { key: 'snombre', label: 'Nombre Contacto', type: 'text', group: 'Contacto', visible: false, order: 11, placeholder: 'Nombre del contacto', minLength: 2, maxLength: 150, errorTip: 'Mínimo 2 caracteres, máximo 150' },
  { key: 'sarea', label: 'Área', type: 'text', group: 'Contacto', visible: false, order: 12, placeholder: 'Ej. Administración', maxLength: 100, errorTip: 'Máximo 100 caracteres' },
  { key: 'idBroker', label: 'Broker', type: 'select', group: 'Contacto', visible: false, order: 13, placeholder: 'Selecciona broker', errorTip: 'Selecciona un broker' },
  { key: 'itelefono', label: 'Teléfono', type: 'text', group: 'Contacto', visible: false, order: 14, placeholder: 'Ej. +52 55 1234 5678', pattern: PHONE_REGEX, errorTip: 'Teléfono inválido (7-15 dígitos)' },
  { key: 'scorreo', label: 'Correo Contacto', type: 'text', group: 'Contacto', visible: false, order: 15, placeholder: 'correo@ejemplo.com', pattern: EMAIL_REGEX, maxLength: 150, errorTip: 'Correo inválido' },
];

@Component({
  selector: 'app-prospect-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    CdkDrag,
    CdkDragPlaceholder,
    CdkDropList,
    NzModalModule,
    NzFormModule,
    NzInputModule,
    NzInputNumberModule,
    NzButtonModule,
    NzSpinModule,
    NzIconModule,
    NzTooltipModule,
    NzAlertModule,
    NzSelectModule,
    NzDatePickerModule,
    NzTimePickerModule,
    ModalTitle,
    SectionHeader,
  ],
  templateUrl: './prospect-modal.html',
  styleUrl: './prospect-modal.css',
})
export class ProspectModal implements OnChanges, OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private prospectService = inject(ProspectService);
  private catalogService = inject(CatalogService);
  private catalogCore = inject(CatalogCoreService);
  private cifService = inject(CifService);
  private fileManagerService = inject(FileManagerService);
  private notifyService = inject(NotifyService);
  readonly googleCal = inject(GoogleCalendarService);

  private readonly destroy$ = new Subject<void>();

  @Input() visible = false;
  @Input() prospect: VEtapaProspectoDTO | null = null;
  @Input() prefillDate: Date | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  saving = false;
  loadingData = false;
  private savedArchivos: FileDTO[] | null = null;
  private savedDatosJson: NonNullable<Prospect['datosJson']> | null = null;
  private savedIdBroker: number | null = null;
  connectingGoogle = false;
  googleConnected = false;
  taxRegimeOptions: { value: number; label: string }[] = [];
  loadingTaxRegimes = false;
  brokerOptions: { value: number; label: string }[] = [];
  loadingBrokers = false;

  /** Estado del drop-zone de CIF. */
  cifDragging = false;
  cifUploading = false;
  cifError: string | null = null;
  cifFileName: string | null = null;
  /** Id del archivo ya subido al FileManager para la Constancia Fiscal. */
  private cifUploadedKid: number | null = null;

  /** Campos fijos que siempre se muestran */
  fixedForm: FormGroup = this.fb.group({
    nombre: ['', [Validators.required, Validators.minLength(2)]],
    rfc: ['', [Validators.pattern(RFC_REGEX)]],
    email: ['', [Validators.pattern(EMAIL_REGEX)]],
  });

  /** Campos fijos de agenda (solo en alta) */
  agendaForm = this.fb.group({
    fecha: [null as Date | null, Validators.required],
    hora: [null as Date | null, Validators.required],
    duracion: [null as number | null],
    slink: ['', [Validators.pattern(MEETING_LINK_REGEX)]],
  });

  /** Campos extra que el usuario arrastró al formulario */
  activeFields: DynamicFormFieldConfig[] = [];
  /** Campos disponibles en el panel para arrastrar */
  availableFields: DynamicFormFieldConfig[] = [];
  /** FormGroup dinámico solo para los campos extra activos */
  extraForm = new FormGroup({});

  get isEdit(): boolean {
    return this.prospect !== null;
  }

  disabledDate = (current: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return current.getTime() < today.getTime();
  };

  disabledHours = (): number[] => {
    if (!this.isSelectedDateToday()) return [];
    const now = new Date();
    let minHour = now.getHours();
    if (Math.ceil(now.getMinutes() / 10) * 10 >= 60) minHour++;
    return Array.from({ length: minHour }, (_, i) => i);
  };

  disabledMinutes = (hour: number): number[] => {
    if (!this.isSelectedDateToday()) return [];
    const now = new Date();
    if (hour > now.getHours()) return [];
    if (hour < now.getHours()) return Array.from({ length: 60 }, (_, i) => i);
    const rounded = Math.ceil(now.getMinutes() / 10) * 10;
    if (rounded >= 60) return Array.from({ length: 60 }, (_, i) => i);
    return Array.from({ length: rounded }, (_, i) => i);
  };

  private isSelectedDateToday(): boolean {
    const fecha = this.agendaForm.value.fecha;
    if (!fecha) return false;
    const today = new Date();
    return fecha.getFullYear() === today.getFullYear()
      && fecha.getMonth() === today.getMonth()
      && fecha.getDate() === today.getDate();
  }

  onDuracionKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowed.includes(event.key) || (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(event.key))) return;
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onDuracionInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/\D/g, '').slice(0, 3);
    if (input.value !== cleaned) {
      input.value = cleaned;
    }
    const num = cleaned ? parseInt(cleaned, 10) : null;
    this.agendaForm.patchValue({ duracion: num }, { emitEvent: false });
  }

  ngOnInit(): void {
    this.googleCal.accessToken$.pipe(takeUntil(this.destroy$)).subscribe((token) => {
      this.googleConnected = !!token;
      this.updateFormsState();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateFormsState(): void {
    if (this.isEdit) {
      this.fixedForm.enable();
      return;
    }
    if (this.googleConnected) {
      this.fixedForm.enable();
      this.agendaForm.enable();
    } else {
      this.fixedForm.disable();
      this.agendaForm.disable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.resetExtraFields();
      this.fixedForm.reset();
      this.agendaForm.reset();
      this.extraForm = new FormGroup({});
      this.cifDragging = false;
      this.cifUploading = false;
      this.cifError = null;
      this.cifFileName = null;
      this.cifUploadedKid = null;
      this.savedArchivos = null;
      this.savedDatosJson = null;
      this.savedIdBroker = null;
      if (this.prefillDate) {
        const d = this.prefillDate;
        this.agendaForm.patchValue({ fecha: d });
        const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
        if (hasTime) {
          this.agendaForm.patchValue({ hora: d });
        }
      }
      if (this.prospect) {
        this.loadProspectData();
      }
      this.updateFormsState();
    }
  }

  // ─────────────── Drop zone CIF ───────────────

  onCifDragOver(event: DragEvent): void {
    if (this.cifUploading) return;
    event.preventDefault();
    event.stopPropagation();
    this.cifDragging = true;
  }

  onCifDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cifDragging = false;
  }

  async onCifDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.cifDragging = false;
    if (this.cifUploading) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) await this.processCifFile(file);
  }

  async onCifFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) await this.processCifFile(file);
  }

  clearCifFile(event?: Event): void {
    event?.stopPropagation();
    this.cifFileName = null;
    this.cifError = null;
    this.cifUploadedKid = null;
  }

  private async processCifFile(file: File): Promise<void> {
    const MAX_BYTES = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const ALLOWED_EXT = /\.(pdf|png|jpe?g)$/i;

    if (file.size > MAX_BYTES) {
      this.cifError = 'El archivo excede los 10 MB';
      this.cifFileName = null;
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXT.test(file.name)) {
      this.cifError = 'Sólo se aceptan PDF o imágenes (PNG/JPG)';
      this.cifFileName = null;
      return;
    }

    this.cifError = null;
    this.cifFileName = file.name;
    this.cifUploading = true;

    const previousKid = this.getPreviousCifKid();
    this.cifUploadedKid = null;

    try {
      const [cifResponse, uploadedKid] = await Promise.all([
        this.cifService.procesarCif(file),
        previousKid != null
          ? this.updateCifInFileManager(file, previousKid)
          : this.uploadCifToFileManager(file),
      ]);
      this.cifUploadedKid = uploadedKid;
      await this.applyCifResponse(cifResponse);
    } catch (err) {
      this.cifError = err instanceof Error ? err.message : 'Error al procesar la CIF';
      this.cifUploadedKid = null;
    } finally {
      this.cifUploading = false;
    }
  }

  private getPreviousCifKid(): number | null {
    if (this.cifUploadedKid != null) return this.cifUploadedKid;
    const existing = this.savedArchivos?.find(a => a.tipo === CONSTANCIA_FISCAL_TYPE_ID);
    return existing?.kiddocumento ?? null;
  }

  private async uploadCifToFileManager(file: File): Promise<number> {
    const formData = new FormData();
    formData.append('IdSystem', '1');
    formData.append('IdFileType', CONSTANCIA_FISCAL_TYPE_ID.toString());
    formData.append('File', file, file.name);
    const response = await this.fileManagerService.agregateFiles(formData);
    const pathId = response.object?.pathId;
    if (!pathId) throw new Error('No se recibió el ID del archivo de la Constancia Fiscal');
    return pathId;
  }

  private async updateCifInFileManager(file: File, idFile: number): Promise<number> {
    const formData = new FormData();
    formData.append('idFile', idFile.toString());
    formData.append('IdSystem', '1');
    formData.append('IdFileType', CONSTANCIA_FISCAL_TYPE_ID.toString());
    formData.append('File', file, file.name);
    const response = await this.fileManagerService.updateFile(formData);
    const pathId = response.object?.pathId;
    if (!pathId) throw new Error('No se recibió el ID del archivo de la Constancia Fiscal');
    return pathId;
  }

  /** Mapea la respuesta del webhook a los controles del form (Prospecto group únicamente). */
  private async applyCifResponse(response: ProcesarCifResponse): Promise<void> {
    // Campos fijos (siempre visibles)
    const fixedPatch: Partial<{ nombre: string; rfc: string }> = {};
    if (response.srazonsocial?.trim()) fixedPatch.nombre = response.srazonsocial.trim();
    if (response.srfc?.trim()) fixedPatch.rfc = response.srfc.trim();
    if (Object.keys(fixedPatch).length) this.fixedForm.patchValue(fixedPatch);

    // Campos dinámicos del grupo Prospecto (Contacto y Propuesta se capturan a mano)
    const values: Record<string, unknown> = {};
    if (response.snombrecomercial?.trim()) values['snombrecomercial'] = response.snombrecomercial.trim();

    const actividad = response.actividadeconomica?.actividad?.[0]?.trim();
    if (actividad) values['sactividad'] = actividad;

    const dom = response.domicililo;
    if (dom) {
      if (dom.codigopostal?.trim()) values['scodigopostal'] = dom.codigopostal.trim();
      const calle = [dom.tipodecalle, dom.nombredevialidad]
        .map(s => s?.trim())
        .filter((s): s is string => !!s)
        .join(' ');
      if (calle) values['sdireccion'] = calle;
      if (dom.numeroexterior?.trim()) values['snumeroexterior'] = dom.numeroexterior.trim();
      if (dom.numerointerior?.trim()) values['snumerointerior'] = dom.numerointerior.trim();
      if (dom.nombredelacolonia?.trim()) values['scolonia'] = dom.nombredelacolonia.trim();
      if (dom.nombredelalocalidad?.trim()) values['slocalidad'] = dom.nombredelalocalidad.trim();
      if (dom.nombredelmunicipioodelegacionterritorial?.trim()) {
        values['smunicipio'] = dom.nombredelmunicipioodelegacionterritorial.trim();
      }
    }

    // Regímenes fiscales: match por descripción en lowercase contra el catálogo
    const regResp = response.regimenes?.regimen?.filter((r): r is string => !!r?.trim()) ?? [];
    if (regResp.length > 0) {
      await this.loadTaxRegimeOptions();
      const matched: number[] = [];
      for (const regRaw of regResp) {
        const target = regRaw.trim().toLowerCase();
        const hit = this.taxRegimeOptions.find(opt => {
          const desc = (opt.label.split(' - ')[1] ?? opt.label).trim().toLowerCase();
          return desc === target;
        });
        if (hit) matched.push(hit.value);
      }
      if (matched.length) values['regimenesfiscales'] = matched;
    }

    // Activar fields del panel que tengan valor nuevo y patchear todos
    for (const key of Object.keys(values)) {
      const idx = this.availableFields.findIndex(f => f.key === key);
      if (idx !== -1) {
        const [field] = this.availableFields.splice(idx, 1);
        this.activeFields.push(field);
        this.addExtraControl(field);
      }
    }
    this.extraForm.patchValue(values);
  }

  // ─────────────── Drag-drop de campos dinámicos ───────────────

  /** Cuando se suelta un campo en la zona del formulario (desde el panel o reordenar) */
  onDropToForm(event: CdkDragDrop<DynamicFormFieldConfig[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.activeFields, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(this.availableFields, this.activeFields, event.previousIndex, event.currentIndex);
      this.addExtraControl(this.activeFields[event.currentIndex]);
    }
  }

  /** Cuando se suelta un campo de vuelta al panel */
  onDropToPanel(event: CdkDragDrop<DynamicFormFieldConfig[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.availableFields, event.previousIndex, event.currentIndex);
    } else {
      const field = this.activeFields[event.previousIndex];
      transferArrayItem(this.activeFields, this.availableFields, event.previousIndex, event.currentIndex);
      this.removeExtraControl(field);
    }
  }

  /** Quitar un campo activo con el botón X */
  removeField(index: number): void {
    const [field] = this.activeFields.splice(index, 1);
    this.availableFields.push(field);
    this.removeExtraControl(field);
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  async save(): Promise<void> {
    const agendaInvalid = !this.isEdit && this.agendaForm.invalid;

    if (this.fixedForm.invalid || this.extraForm.invalid || agendaInvalid) {
      for (const c of Object.values(this.fixedForm.controls)) {
        c.markAsDirty();
        c.updateValueAndValidity({ onlySelf: true });
      }
      for (const key of Object.keys(this.extraForm.controls)) {
        const ctrl = this.extraForm.get(key)!;
        ctrl.markAsDirty();
        ctrl.updateValueAndValidity({ onlySelf: true });
      }
      if (!this.isEdit) {
        for (const c of Object.values(this.agendaForm.controls)) {
          c.markAsDirty();
          c.updateValueAndValidity({ onlySelf: true });
        }
      }
      return;
    }

    const fixed = this.fixedForm.value;
    const extra = this.extraForm.value as Record<string, unknown>;
    this.saving = true;

    try {
      const datosJson = this.buildDatosJson(extra);
      const idBrokerForm = (extra['idBroker'] as number | null | undefined) ?? null;

      if (this.isEdit) {
        await this.prospectService.updateProspect({
          idProspect: this.prospect!.fkIdProspecto!,
          nombre: fixed.nombre || null,
          rfc: fixed.rfc || null,
          email: fixed.email || null,
          idBroker: idBrokerForm,
          datosJson,
        });
        this.notifyService.successNotyfy('Éxito', 'Prospecto actualizado');
      } else {
        const res = await this.prospectService.createProspect({
          nombre: fixed.nombre,
          rfc: fixed.rfc || null,
          email: fixed.email || null,
          idBroker: idBrokerForm,
          datosJson,
        });

        if (res?.idProspec) {
          const agenda = this.agendaForm.getRawValue();
          const fecha = agenda.fecha!;
          const hora = agenda.hora!;
          fecha.setHours(hora.getHours(), hora.getMinutes(), 0, 0);
          const slink = (agenda.slink ?? '').trim() || null;

          // Google Calendar primero para obtener el eventId
          let sgoogleEventId: string | null = null;
          try {
            const duracionMin = agenda.duracion ?? 60;
            const endDate = new Date(fecha.getTime() + duracionMin * 60 * 1000);
            const tz = this.googleCal.getUserTimeZone();

            const gcEvent = await firstValueFrom(this.googleCal.createEvent(environment.googleCalendarId, {
              summary: `Sesión de presentación — ${fixed.nombre}`,
              description: this.buildCalendarDescription(fixed, extra, slink),
              start: { dateTime: fecha.toISOString(), timeZone: tz },
              end: { dateTime: endDate.toISOString(), timeZone: tz },
            }));
            sgoogleEventId = gcEvent.id ?? null;
          } catch {
            this.notifyService.errorNotyfy('El prospecto se creó, pero no se pudo agregar a Google Calendar');
          }

          await this.prospectService.createAgenda({
            fkidprospecto: res.idProspec,
            scomentario: this.buildCalendarDescription(fixed, extra, slink),
            ffechaagenda: toLocalISOString(fecha),
            sgoogleEventId,
            slink,
          });
        }

        this.notifyService.successNotyfy('Éxito', 'Prospecto creado y agenda registrada');
      }
      this.saved.emit();
      this.close();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al guardar prospecto';
      this.notifyService.errorNotyfy(msg);
    } finally {
      this.saving = false;
    }
  }

  async connectGoogle(): Promise<void> {
    this.connectingGoogle = true;
    try {
      await this.googleCal.signIn();
      //this.notifyService.successNotyfy('Google', 'Sesión iniciada correctamente');
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      const cancelled = msg.toLowerCase().includes('popup') || msg.toLowerCase().includes('cancel');
      if (!cancelled) {
        this.notifyService.errorNotyfy(msg || 'Error al conectar con Google');
      }
    } finally {
      this.connectingGoogle = false;
    }
  }

  private buildCalendarDescription(
    fixed: Record<string, unknown>,
    extra: Record<string, unknown>,
    slink?: string | null,
  ): string {
    const lines: string[] = [`Sesión de presentación para prospecto ${fixed['nombre']}`];

    const fixedLines: string[] = [];
    if (fixed['rfc']) fixedLines.push(`• RFC: ${fixed['rfc']}`);
    if (fixed['email']) fixedLines.push(`• Email: ${fixed['email']}`);
    if (fixedLines.length) {
      lines.push(...fixedLines);
    }
    lines.push('');

    const contactLines: string[] = [];
    if (extra['snombre']) contactLines.push(`• Nombre: ${extra['snombre']}`);
    if (extra['sarea']) contactLines.push(`• Área: ${extra['sarea']}`);
    const idBroker = extra['idBroker'] as number | null | undefined;
    if (idBroker != null) {
      const broker = this.brokerOptions.find(b => b.value === idBroker);
      if (broker) contactLines.push(`• Broker: ${broker.label}`);
    }
    if (extra['itelefono']) contactLines.push(`• Teléfono: ${extra['itelefono']}`);
    if (extra['scorreo']) contactLines.push(`• Correo: ${extra['scorreo']}`);

    if (contactLines.length) {
      lines.push('Contacto:');
      lines.push(...contactLines);
      lines.push('');
    }

    const prospectFields = [
      { key: 'snombrecomercial', label: 'Nombre Comercial' },
      { key: 'sactividad', label: 'Actividad' },
      { key: 'iempleados', label: 'Empleados' },
      { key: 'scodigopostal', label: 'C.P.' },
      { key: 'sdireccion', label: 'Dirección' },
      { key: 'snumeroexterior', label: 'Núm. Ext.' },
      { key: 'snumerointerior', label: 'Núm. Int.' },
      { key: 'scolonia', label: 'Colonia' },
      { key: 'slocalidad', label: 'Localidad' },
      { key: 'smunicipio', label: 'Municipio' },
    ];
    const prospectLines = prospectFields
      .filter(f => extra[f.key])
      .map(f => `• ${f.label}: ${extra[f.key]}`);

    const regs = extra['regimenesfiscales'] as number[] | undefined;
    if (regs?.length) {
      const labels = regs
        .map(id => this.taxRegimeOptions.find(r => r.value === id)?.label ?? String(id))
        .join(', ');
      prospectLines.push(`• Regímenes Fiscales: ${labels}`);
    }

    if (prospectLines.length) {
      lines.push('Empresa:');
      lines.push(...prospectLines);
      lines.push('');
    }

    if (slink) {
      if (lines[lines.length - 1] !== '') lines.push('');
      lines.push('Reunión:');
      lines.push(`• Enlace: ${slink}`);
    }

    return lines.join('\n');
  }

  private resetExtraFields(): void {
    this.activeFields = [];
    this.availableFields = EXTRA_FIELDS.map(f => ({ ...f }));
  }

  private addExtraControl(field: DynamicFormFieldConfig): void {
    const validators = [];
    if (field.required) validators.push(Validators.required);
    if (field.pattern) validators.push(Validators.pattern(field.pattern));
    if (field.minLength != null) validators.push(Validators.minLength(field.minLength));
    if (field.maxLength != null) validators.push(Validators.maxLength(field.maxLength));
    if (field.min != null) validators.push(Validators.min(field.min));
    if (field.max != null) validators.push(Validators.max(field.max));
    let initial: unknown;
    switch (field.type) {
      case 'number':  initial = null; break;
      case 'select':  initial = field.selectMode === 'multiple' ? [] : null; break;
      default:        initial = ''; break;
    }
    this.extraForm.addControl(field.key, new FormControl(initial, validators));
    if (field.key === 'regimenesfiscales') this.loadTaxRegimeOptions();
    if (field.key === 'idBroker') this.loadBrokerOptions();
  }

  private async loadTaxRegimeOptions(): Promise<void> {
    if (this.taxRegimeOptions.length > 0) return;
    this.loadingTaxRegimes = true;
    try {
      const res = await this.catalogCore.getFiscalRegimes();
      this.taxRegimeOptions = (res as GenericCatalog[]).map(r => ({
        value: r.id!,
        label: `${r.id} - ${r.description}`,
      }));
    } catch { /* silent */ } finally {
      this.loadingTaxRegimes = false;
    }
  }

  private async loadBrokerOptions(): Promise<void> {
    if (this.brokerOptions.length > 0) return;
    this.loadingBrokers = true;
    try {
      const res = await this.catalogCore.getBrokers();
      this.brokerOptions = (res as GenericCatalog[]).map(r => ({
        value: r.id!,
        label: `${r.id} - ${r.description}`,
      }));
    } catch { /* silent */ } finally {
      this.loadingBrokers = false;
    }
  }

  private removeExtraControl(field: DynamicFormFieldConfig): void {
    this.extraForm.removeControl(field.key);
  }

  private async loadProspectData(): Promise<void> {
    if (!this.prospect?.fkIdProspecto) return;
    this.loadingData = true;
    try {
      const p = await this.prospectService.byId(this.prospect.fkIdProspecto) as Prospect | null;
      if (!p) {
        this.patchBasicFromView();
        return;
      }
      this.fixedForm.patchValue({
        nombre: p.nombre ?? this.prospect.sprospecto ?? '',
        rfc: p.rfc ?? '',
        email: p.email ?? '',
      });
      this.savedDatosJson = p.datosJson ?? null;
      this.savedArchivos = p.datosJson?.archivos ?? null;
      this.savedIdBroker = p.rel_Prospect_Broker?.idBroker ?? null;
      if (p.datosJson) {
        this.activateFieldsFromData(p.datosJson);
      }
    } catch {
      this.patchBasicFromView();
    } finally {
      this.loadingData = false;
    }
  }

  /** Al editar, activa automáticamente los campos que ya tienen datos */
  private activateFieldsFromData(datosJson: NonNullable<Prospect['datosJson']>): void {
    const values: Record<string, unknown> = {};
    const { prospecto, contacto } = datosJson;

    if (prospecto) {
      if (prospecto.snombrecomercial) values['snombrecomercial'] = prospecto.snombrecomercial;
      if (prospecto.sactividad) values['sactividad'] = prospecto.sactividad;
      if (prospecto.iempleados != null) values['iempleados'] = prospecto.iempleados;
      if (prospecto.scodigopostal) values['scodigopostal'] = prospecto.scodigopostal;
      if (prospecto.sdireccion) values['sdireccion'] = prospecto.sdireccion;
      if (prospecto.snumeroexterior) values['snumeroexterior'] = prospecto.snumeroexterior;
      if (prospecto.snumerointerior) values['snumerointerior'] = prospecto.snumerointerior;
      if (prospecto.scolonia) values['scolonia'] = prospecto.scolonia;
      if (prospecto.slocalidad) values['slocalidad'] = prospecto.slocalidad;
      if (prospecto.smunicipio) values['smunicipio'] = prospecto.smunicipio;
      if (prospecto.regimenesfiscales?.length) values['regimenesfiscales'] = prospecto.regimenesfiscales;
    }
    if (contacto) {
      if (contacto.snombre) values['snombre'] = contacto.snombre;
      // sarea no vive en JsonProspectoContactType (schema de lectura); sólo en
      // ProspectContact (escritura). Se lee defensivo para cuando backend lo exponga.
      const contactoArea = (contacto as { sarea?: string | null }).sarea;
      if (contactoArea) values['sarea'] = contactoArea;
      if (contacto.itelefono) values['itelefono'] = contacto.itelefono;
      if (contacto.scorreo) values['scorreo'] = contacto.scorreo;
    }
    if (this.savedIdBroker != null) values['idBroker'] = this.savedIdBroker;

    // Mover campos con datos del panel al formulario
    for (const key of Object.keys(values)) {
      const idx = this.availableFields.findIndex(f => f.key === key);
      if (idx !== -1) {
        const [field] = this.availableFields.splice(idx, 1);
        this.activeFields.push(field);
        this.addExtraControl(field);
      }
    }
    this.extraForm.patchValue(values);
  }

  private patchBasicFromView(): void {
    this.fixedForm.patchValue({
      nombre: this.prospect?.sprospecto ?? '',
      rfc: '',
      email: '',
    });
  }

  private buildDatosJson(v: Record<string, unknown>) {
    const archivos = this.getEffectiveArchivos();

    const regs = v['regimenesfiscales'] as number[] | undefined;
    const prospectoPatch = {
      snombrecomercial: (v['snombrecomercial'] as string) || null,
      sactividad: (v['sactividad'] as string) || null,
      iempleados: (v['iempleados'] as number) || null,
      scodigopostal: (v['scodigopostal'] as string) || null,
      sdireccion: (v['sdireccion'] as string) || null,
      snumeroexterior: (v['snumeroexterior'] as string) || null,
      snumerointerior: (v['snumerointerior'] as string) || null,
      scolonia: (v['scolonia'] as string) || null,
      slocalidad: (v['slocalidad'] as string) || null,
      smunicipio: (v['smunicipio'] as string) || null,
      regimenesfiscales: regs?.length ? regs : null,
    };
    const contactoPatch = {
      snombre: (v['snombre'] as string) || null,
      sarea: (v['sarea'] as string) || null,
      itelefono: (v['itelefono'] as string) || null,
      scorreo: (v['scorreo'] as string) || null,
    };

    if (this.isEdit) {
      // Edición: mergear sobre lo que vino de byId. prospecto.idInplant y
      // spropuesta se preservan vía spread. vendedores y archivos requieren
      // mapeo del schema de lectura (fk*) al de escritura (idBroker, etc.).
      // convertProspectToClienteRequest se excluye porque su shape de read
      // (snake_case) no es compatible con el write (camelCase) y este modal
      // no lo gestiona — el flujo de aceptación es responsable.
      const {
        vendedores,
        archivos: _readArchivos,
        convertProspectToClienteRequest: _readConvert,
        ...restSaved
      } = this.savedDatosJson ?? {};
      const writeVendedores = vendedores?.map(v => ({
        idBroker: v.fkidbroker ?? 0,
        idImplant: v.fkidimplant ?? 0,
        comisionPorcentaje: v.ncomisionporcentaje ?? 0,
      }));
      return {
        ...restSaved,
        prospecto: { ...(this.savedDatosJson?.prospecto ?? {}), ...prospectoPatch },
        contacto: { ...(this.savedDatosJson?.contacto ?? {}), ...contactoPatch },
        ...(writeVendedores?.length ? { vendedores: writeVendedores } : {}),
        ...(archivos?.length ? { archivos } : {}),
      };
    }

    // Alta: solo bloques desde el form.
    if (this.activeFields.length === 0 && !archivos?.length) return undefined;

    const hasProspecto = v['snombrecomercial'] || v['sactividad'] || v['iempleados']
      || v['scodigopostal'] || v['sdireccion'] || v['snumeroexterior']
      || v['snumerointerior'] || v['scolonia'] || v['slocalidad']
      || v['smunicipio'] || regs?.length;
    const hasContacto = v['snombre'] || v['sarea']
      || v['itelefono'] || v['scorreo'];

    if (!hasProspecto && !hasContacto && !archivos?.length) return undefined;

    return {
      ...(hasProspecto ? { prospecto: prospectoPatch } : {}),
      ...(hasContacto ? { contacto: contactoPatch } : {}),
      ...(archivos?.length ? { archivos } : {}),
    };
  }

  /** Mezcla los archivos ya guardados del prospecto con el CIF recién subido (reemplaza Constancia Fiscal). */
  private getEffectiveArchivos(): FileDTO[] | null {
    const base: FileDTO[] = this.savedArchivos ? [...this.savedArchivos] : [];
    if (this.cifUploadedKid == null) {
      return base.length ? base : null;
    }
    const filtered = base.filter(a => a.tipo !== CONSTANCIA_FISCAL_TYPE_ID);
    filtered.push({ tipo: CONSTANCIA_FISCAL_TYPE_ID, kiddocumento: this.cifUploadedKid });
    return filtered;
  }
}
